var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { gql } from '@apollo/client';
import { DateTime } from 'luxon';
import { areDatesEqual, datesRemainingInInterval, DayOfWeekMappings, getSwapRequestsForDayFromMap, getWeekIntervalDates, useAssociateContext } from '@amzn/atoz-shiftapp-utilities';
import { completedSwapsToDaysMap as generateCompletedSwapsToDaysMap, pendingSwapsToDaysMap as generatePendingSwapsToDaysMap, completedSwapRequestsToDaysMap as generateCompletedSwapRequestsToDaysMap, pendingSwapRequestsToDaysMap as generatePendingSwapRequestsToDaysMap, processingSwapRequestsToDaysMap as generateProcessingSwapRequestsToDaysMap, shiftHasSwapRequest } from '../../../../utils';
export var weekHasCompletedSwap = function (completedSwapsToDaysMap, shiftStartTime) {
    return Object.keys(completedSwapsToDaysMap).some(function (completedSwapDay) {
        var _a = getWeekIntervalDates(DateTime.fromISO(completedSwapDay)), weekStart = _a.weekStart, weekEnd = _a.weekEnd;
        return shiftStartTime >= weekStart && shiftStartTime <= weekEnd;
    });
};
export var weekHasCompletedSwapRequest = function (completedSwapRequestsToDaysMap, shiftStartTime) {
    return Object.keys(completedSwapRequestsToDaysMap).some(function (completedSwapRequestDay) {
        var _a = getWeekIntervalDates(DateTime.fromISO(completedSwapRequestDay)), weekStart = _a.weekStart, weekEnd = _a.weekEnd;
        return shiftStartTime >= weekStart && shiftStartTime <= weekEnd;
    });
};
export var weekHasProcessingSwapRequest = function (proccessingSwapRequestsToDaysMap, shiftStartTime) {
    return Object.keys(proccessingSwapRequestsToDaysMap).some(function (processingSwapRequestDay) {
        var _a = getWeekIntervalDates(DateTime.fromISO(processingSwapRequestDay)), weekStart = _a.weekStart, weekEnd = _a.weekEnd;
        return shiftStartTime >= weekStart && shiftStartTime <= weekEnd;
    });
};
export var weekHasDayWithoutShift = function (daysWithShift, shiftWeekInterval, swapRequestBoundariesStart) {
    var scheduledDaysWithShiftInCurrentWeek = daysWithShift.filter(function (day) {
        return shiftWeekInterval.contains(day);
    });
    var daysLeftInCurrentWeek = datesRemainingInInterval(swapRequestBoundariesStart, shiftWeekInterval);
    var remainingDaysWithoutShifts = daysLeftInCurrentWeek.filter(function (day) {
        return !scheduledDaysWithShiftInCurrentWeek.some(function (scheduledDate) {
            return areDatesEqual(day, scheduledDate);
        });
    });
    return !!(remainingDaysWithoutShifts === null || remainingDaysWithoutShifts === void 0 ? void 0 : remainingDaysWithoutShifts.length);
};
export var isShiftWithinSwapBoundaries = function (shiftStartDateTime, swapRequestBoundaries) {
    if (!swapRequestBoundaries) {
        return true;
    }
    else {
        return (shiftStartDateTime >
            DateTime.fromISO(swapRequestBoundaries.start) &&
            shiftStartDateTime < DateTime.fromISO(swapRequestBoundaries.end));
    }
};
export var isSwapRestricted = function (shiftStartDateTime, completedSwapsToDaysMap, swapOutRestriction) {
    if (!swapOutRestriction) {
        return false;
    }
    if (!swapOutRestriction.restrictedDaysOfWeek.includes(DayOfWeekMappings[shiftStartDateTime.weekdayShort])) {
        return false;
    }
    var restrictionWindowStart = getWeekIntervalDates(shiftStartDateTime.minus({
        weeks: swapOutRestriction.restrictionForNumberOfWeeks
    })).weekStart;
    var restrictionWindowEnd = getWeekIntervalDates(shiftStartDateTime.plus({
        weeks: swapOutRestriction.restrictionForNumberOfWeeks
    })).weekEnd;
    return Object.keys(completedSwapsToDaysMap).some(function (completedSwapDay) {
        var swapWorkdayDate = DateTime.fromISO(completedSwapDay);
        return (swapOutRestriction.restrictedDaysOfWeek.includes(DayOfWeekMappings[swapWorkdayDate.weekdayShort]) &&
            restrictionWindowStart <= swapWorkdayDate &&
            restrictionWindowEnd >= swapWorkdayDate);
    });
};
export var isSwapRequestRestricted = function (shiftStartDateTime, completedSwapRequestsToDaysMap, swapOutRestriction) {
    if (!swapOutRestriction) {
        return false;
    }
    if (!swapOutRestriction.restrictedDaysOfWeek.includes(DayOfWeekMappings[shiftStartDateTime.weekdayShort])) {
        return false;
    }
    var restrictionWindowStart = getWeekIntervalDates(shiftStartDateTime.minus({
        weeks: swapOutRestriction.restrictionForNumberOfWeeks
    })).weekStart;
    var restrictionWindowEnd = getWeekIntervalDates(shiftStartDateTime.plus({
        weeks: swapOutRestriction.restrictionForNumberOfWeeks
    })).weekEnd;
    return Object.keys(completedSwapRequestsToDaysMap).some(function (completedSwapRequestDay) {
        var swapWorkdayDate = DateTime.fromISO(completedSwapRequestDay);
        return (swapOutRestriction.restrictedDaysOfWeek.includes(DayOfWeekMappings[swapWorkdayDate.weekdayShort]) &&
            restrictionWindowStart <= swapWorkdayDate &&
            restrictionWindowEnd >= swapWorkdayDate);
    });
};
var bothButtonsFalse = {
    showSwapRequestButton: false,
    showSwapCancelButton: false
};
export var useSwapButtons = function (_a) {
    var _b, _c;
    var swaps = _a.swaps, swapRequests = _a.swapRequests, scheduleShift = _a.scheduleShift, siteSwapConfiguration = _a.siteSwapConfiguration, daysWithShift = _a.daysWithShift;
    var features = useAssociateContext().features;
    var _d = siteSwapConfiguration || {}, swapEnabled = _d.swapEnabled, swapRequestBoundaries = _d.swapRequestBoundaries, blockedDates = _d.blockedDates, swapOutRestriction = _d.swapOutRestriction;
    /*  Currently the shift-exchange team is in the process of migrating to shift match and
        the swap requests variable will hold the correct swaps data moving forwards.
        In the meantime we are maintaining both swaps and swapRequests and the variable used
        will be denoted by the swapApp feature value
    */
    var noEligibleSwaps = features.swapApp ? !swapRequests : !swaps;
    if (!scheduleShift || !swapEnabled || noEligibleSwaps) {
        return bothButtonsFalse;
    }
    var shiftStartTime = scheduleShift.shift.timeRange.start;
    var shiftStartDateTime = DateTime.fromISO(shiftStartTime);
    var completedSwapRequestsToDaysMap = generateCompletedSwapRequestsToDaysMap(swapRequests === null || swapRequests === void 0 ? void 0 : swapRequests.swaps);
    var proccessingSwapRequestsToDaysMap = generateProcessingSwapRequestsToDaysMap(swapRequests === null || swapRequests === void 0 ? void 0 : swapRequests.swaps);
    var pendingSwapRequestsToDaysMap = generatePendingSwapRequestsToDaysMap(swapRequests === null || swapRequests === void 0 ? void 0 : swapRequests.swaps);
    var pendingSwapRequestsForDay = getSwapRequestsForDayFromMap(pendingSwapRequestsToDaysMap, shiftStartDateTime);
    var dayHasPendingSwapRequests = !!(pendingSwapRequestsForDay === null || pendingSwapRequestsForDay === void 0 ? void 0 : pendingSwapRequestsForDay.length);
    var shiftHasPendingSwapRequests = dayHasPendingSwapRequests &&
        shiftHasSwapRequest(pendingSwapRequestsForDay, scheduleShift.shift.timeRange.start);
    if (dayHasPendingSwapRequests) {
        return {
            showSwapRequestButton: false,
            showSwapCancelButton: shiftHasPendingSwapRequests
        };
    }
    // TODO: Legacy code that will be removed once shift match migration has been completed
    var completedSwapsToDaysMap = generateCompletedSwapsToDaysMap(swaps === null || swaps === void 0 ? void 0 : swaps.swaps);
    var pendingSwapsToDaysMap = generatePendingSwapsToDaysMap(swaps === null || swaps === void 0 ? void 0 : swaps.swaps);
    var hasPendingSwaps = !!((_b = pendingSwapsToDaysMap[shiftStartDateTime.toISODate()]) === null || _b === void 0 ? void 0 : _b.length);
    if (hasPendingSwaps) {
        return {
            showSwapRequestButton: false,
            showSwapCancelButton: true
        };
    }
    var isWithinSwapRequestBoundaries = isShiftWithinSwapBoundaries(shiftStartDateTime, swapRequestBoundaries);
    if (!isWithinSwapRequestBoundaries) {
        return bothButtonsFalse;
    }
    var doesWeekHasCompletedSwaps = features.swapApp
        ? weekHasCompletedSwapRequest(completedSwapRequestsToDaysMap, shiftStartDateTime)
        : weekHasCompletedSwap(completedSwapsToDaysMap, shiftStartDateTime);
    if (doesWeekHasCompletedSwaps) {
        return bothButtonsFalse;
    }
    var doesWeekHaveProcessingSwaps = features.swapApp
        ? weekHasProcessingSwapRequest(proccessingSwapRequestsToDaysMap, shiftStartDateTime)
        : false;
    if (doesWeekHaveProcessingSwaps) {
        return bothButtonsFalse;
    }
    // If the date is blocked, do not display the swap button
    var isDayBlocked = (_c = blockedDates === null || blockedDates === void 0 ? void 0 : blockedDates.some(function (blockedDate) { return blockedDate === shiftStartDateTime.toISODate(); })) !== null && _c !== void 0 ? _c : false;
    if (isDayBlocked) {
        return bothButtonsFalse;
    }
    var isDayRestricted = features.swapApp
        ? isSwapRequestRestricted(shiftStartDateTime, completedSwapRequestsToDaysMap, swapOutRestriction)
        : isSwapRestricted(shiftStartDateTime, completedSwapsToDaysMap, swapOutRestriction);
    if (isDayRestricted) {
        return bothButtonsFalse;
    }
    var today = DateTime.local();
    var weekInterval = getWeekIntervalDates(today).weekInterval;
    var isShiftInCurrentWeek = weekInterval.contains(shiftStartDateTime);
    // If there are only future working days in the same week as now, do not display the swap button
    var daysAvailableToSwapInWeek = isShiftInCurrentWeek
        ? weekHasDayWithoutShift(daysWithShift, weekInterval, swapRequestBoundaries
            ? DateTime.fromISO(swapRequestBoundaries.start)
            : today)
        : true;
    // If it reaches here, showSwapRequestButton = true, showSwapCancelButton = false
    // using variables flags to make it more clear why its like that
    return {
        showSwapRequestButton: !isDayBlocked &&
            isWithinSwapRequestBoundaries &&
            !hasPendingSwaps &&
            !dayHasPendingSwapRequests &&
            !doesWeekHasCompletedSwaps &&
            !doesWeekHaveProcessingSwaps &&
            !isDayRestricted &&
            daysAvailableToSwapInWeek,
        showSwapCancelButton: hasPendingSwaps || shiftHasPendingSwapRequests
    };
};
useSwapButtons.fragments = {
    siteSwapConfiguration: gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n        fragment UseSwapButtons_SiteSwapConfiguration on SiteSwapConfiguration {\n            swapEnabled\n            swapRequestBoundaries {\n                start\n                end\n            }\n            blockedDates\n            swapOutRestriction {\n                restrictedDaysOfWeek\n                restrictionForNumberOfWeeks\n            }\n        }\n    "], ["\n        fragment UseSwapButtons_SiteSwapConfiguration on SiteSwapConfiguration {\n            swapEnabled\n            swapRequestBoundaries {\n                start\n                end\n            }\n            blockedDates\n            swapOutRestriction {\n                restrictedDaysOfWeek\n                restrictionForNumberOfWeeks\n            }\n        }\n    "]))),
    // TODO: Remove once shift match migration has been completed
    swaps: gql(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        fragment UseSwapButtons_Swaps on Swaps {\n            swaps {\n                id\n                swapFrom\n                swapTo\n                status\n            }\n        }\n    "], ["\n        fragment UseSwapButtons_Swaps on Swaps {\n            swaps {\n                id\n                swapFrom\n                swapTo\n                status\n            }\n        }\n    "]))),
    scheduleShift: gql(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n        fragment UseSwapButtons_ScheduleShift on ScheduleShift {\n            shift {\n                timeRange {\n                    start\n                }\n            }\n        }\n    "], ["\n        fragment UseSwapButtons_ScheduleShift on ScheduleShift {\n            shift {\n                timeRange {\n                    start\n                }\n            }\n        }\n    "]))),
    swapRequests: gql(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n        fragment UseSwapButtons_SwapRequests on SwapRequests {\n            swaps {\n                from {\n                    shifts {\n                        timeRange {\n                            start\n                            end\n                        }\n                    }\n                }\n                id\n                status\n                to {\n                    shifts {\n                        timeRange {\n                            start\n                            end\n                        }\n                    }\n                }\n            }\n        }\n    "], ["\n        fragment UseSwapButtons_SwapRequests on SwapRequests {\n            swaps {\n                from {\n                    shifts {\n                        timeRange {\n                            start\n                            end\n                        }\n                    }\n                }\n                id\n                status\n                to {\n                    shifts {\n                        timeRange {\n                            start\n                            end\n                        }\n                    }\n                }\n            }\n        }\n    "])))
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
